// Utilities for applying active discounts to products/cart items.
// Intentionally UI-agnostic so ProductCard, ProductDetails, and CartDrawer stay consistent.

function toNumber(value, fallback = 0) {
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function normalizeDisplayNumber(value) {
  const n = toNumber(value, 0);
  if (!Number.isFinite(n)) return 0;
  if (Number.isInteger(n)) return n;
  return Number(n.toFixed(2));
}

export function discountAppliesToProduct(discount, product) {
  if (!discount || !product) return false;
  if (discount.isActive === false) return false;

  const targetType = discount.targetType;
  const targetId = discount.targetId;

  if (targetType === 'all') return true;

  // Support cart items that store a base product id.
  const productId = product.baseProductId || product.productId || product.id;
  if (targetType === 'product' && targetId && productId && targetId === productId) return true;

  if (targetType === 'category' && targetId) {
    return (
      product.category === targetId ||
      product.categoryId === targetId ||
      (Array.isArray(product.categories) && product.categories.includes(targetId))
    );
  }

  // Coupon discounts are applied at checkout only.
  return false;
}

export function pickPreferredDiscount(current, candidate) {
  if (!current) return candidate;
  if (!candidate) return current;

  const currentValue = toNumber(current.value, 0);
  const candidateValue = toNumber(candidate.value, 0);

  const currentIsPercentage = current.type === 'percentage';
  const candidateIsPercentage = candidate.type === 'percentage';

  // Prefer percentage discounts when types differ.
  if (currentIsPercentage !== candidateIsPercentage) {
    return candidateIsPercentage ? candidate : current;
  }

  // If both are same type, prefer the higher numeric value.
  return candidateValue > currentValue ? candidate : current;
}

export function getBestDiscountForProduct(product, activeDiscounts) {
  if (!product || !Array.isArray(activeDiscounts) || activeDiscounts.length === 0) return null;

  let matched = null;
  for (const discount of activeDiscounts) {
    if (!discountAppliesToProduct(discount, product)) continue;
    matched = pickPreferredDiscount(matched, discount);
  }
  return matched;
}

export function computeDiscountedPrice(basePrice, discount) {
  const base = toNumber(basePrice, 0);
  if (!discount) {
    return { basePrice: base, finalPrice: base, discountAmount: 0 };
  }

  const value = toNumber(discount.value, 0);
  if (base <= 0 || value <= 0) {
    return { basePrice: base, finalPrice: base, discountAmount: 0 };
  }

  let finalPrice = base;
  if (discount.type === 'percentage') {
    finalPrice = base * (1 - value / 100);
  } else if (discount.type === 'fixed') {
    finalPrice = base - value;
  }

  finalPrice = Math.max(0, finalPrice);

  // Keep numeric stability for display and totals.
  const finalRounded = Number(finalPrice.toFixed(2));
  const discountAmount = Number((base - finalRounded).toFixed(2));

  return {
    basePrice: base,
    finalPrice: finalRounded,
    discountAmount: Math.max(0, discountAmount)
  };
}

export function formatDiscountBadge(discount) {
  if (!discount) return null;

  const value = normalizeDisplayNumber(discount.value);
  if (value <= 0) return null;

  if (discount.type === 'percentage') {
    return `${value}% OFF`;
  }

  if (discount.type === 'fixed') {
    return `${value} EGP OFF`;
  }

  return 'OFF';
}
